import * as React from 'react';
import { Dialog } from '@ampm-7en-ai/ui/components/Dialog';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { Field, Input } from '@ampm-7en-ai/ui/components/Input';
import { ToggleRow } from '@ampm-7en-ai/ui/components/ToggleRow';
import { useToast } from '@/hooks/use-toast';
import type { ModelObject } from '@/hooks/useLLMProviders';
import { CURRENCY_SYMBOL } from '@/lib/money';

/**
 * A model on a provider: what it is called, and what it costs to run.
 *
 * Two names, because they are not the same thing — one is what the API
 * expects and one is what a customer reads. Getting the first wrong means
 * calls fail; getting the second wrong just reads badly.
 */
export default function ModelDialogV2({
  open,
  onOpenChange,
  providerId,
  providerName,
  model,
  onAdd,
  onUpdate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerId: number;
  providerName: string;
  /** The one being changed, or null when a new one is being added. */
  model?: ModelObject | null;
  onAdd: (
    providerId: number,
    name: string,
    displayName: string,
    costIn: number | null,
    costOut: number | null,
    premium: boolean
  ) => Promise<void>;
  onUpdate?: (
    modelId: number,
    providerId: number,
    name: string,
    displayName: string,
    costIn: number | null,
    costOut: number | null,
    premium: boolean
  ) => Promise<void>;
}) {
  const { toast } = useToast();
  const editing = Boolean(model);

  const [name, setName] = React.useState('');
  const [displayName, setDisplayName] = React.useState('');
  const [costIn, setCostIn] = React.useState('');
  const [costOut, setCostOut] = React.useState('');
  const [premium, setPremium] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  // The display name follows the model name until someone edits it directly.
  const [displayTouched, setDisplayTouched] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setName(model?.name ?? '');
    setDisplayName(model?.display_name ?? model?.name ?? '');
    setCostIn(model?.cost_per_1m_input_tokens == null ? '' : String(model.cost_per_1m_input_tokens));
    setCostOut(model?.cost_per_1m_output_tokens == null ? '' : String(model.cost_per_1m_output_tokens));
    setPremium(model?.is_premium ?? false);
    setDisplayTouched(Boolean(model?.display_name));
    setSaving(false);
  }, [open, model]);

  const asNumber = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number.parseFloat(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const blocked = !name.trim() || !displayName.trim();

  const save = async () => {
    if (blocked) return;
    setSaving(true);
    try {
      if (editing && model && onUpdate) {
        await onUpdate(model.id, providerId, name.trim(), displayName.trim(), asNumber(costIn), asNumber(costOut), premium);
      } else {
        await onAdd(providerId, name.trim(), displayName.trim(), asNumber(costIn), asNumber(costOut), premium);
      }
      toast({ title: `${displayName.trim()} ${editing ? 'updated' : 'added'}` });
      onOpenChange(false);
    } catch (failure) {
      toast({
        title: 'That didn’t work',
        description: failure instanceof Error ? failure.message : 'The model wasn’t saved.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? 'Edit model' : 'Add a model'}
      description={`On ${providerName || 'this provider'}.`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void save()} disabled={saving || blocked}>
            {saving ? 'Saving…' : editing ? 'Save' : 'Add model'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Model name" htmlFor="model-name" hint="Exactly as the provider's API expects it.">
          <Input
            id="model-name"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              if (!displayTouched) setDisplayName(event.target.value);
            }}
            placeholder="gpt-4o"
            className="font-ui-mono text-ui-sm"
          />
        </Field>

        <Field label="Shown as" htmlFor="model-display" hint="What people pick from a list.">
          <Input
            id="model-display"
            value={displayName}
            onChange={(event) => {
              setDisplayTouched(true);
              setDisplayName(event.target.value);
            }}
            placeholder="GPT-4o"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={`Input (${CURRENCY_SYMBOL} per 1M)`} htmlFor="model-cost-in" hint="Leave empty if it isn’t metered.">
            <Input
              id="model-cost-in"
              type="number"
              min={0}
              step="0.01"
              value={costIn}
              onChange={(event) => setCostIn(event.target.value)}
              placeholder="2.50"
            />
          </Field>
          <Field label={`Output (${CURRENCY_SYMBOL} per 1M)`} htmlFor="model-cost-out">
            <Input
              id="model-cost-out"
              type="number"
              min={0}
              step="0.01"
              value={costOut}
              onChange={(event) => setCostOut(event.target.value)}
              placeholder="10.00"
            />
          </Field>
        </div>

        <ToggleRow
          label="Premium"
          description="Only workspaces on a plan that includes premium models can choose it."
          checked={premium}
          onCheckedChange={setPremium}
        />
      </div>
    </Dialog>
  );
}
